/**
 * The account menu (docs/04 §1b, §3, §5) — identity, the theme and density
 * preferences, and sign out.
 *
 * Density used to sit in the top bar as a permanent button, and Matt's reading
 * of it was fair: it looked like a view control for the list, sat nowhere near
 * one, and appeared on screens where nothing visibly changed. It is not a view
 * control — it is a comfort preference that applies app-wide and gets set once.
 * So it lives here, where set-once preferences belong, and the list toolbar is
 * left holding only the controls that are genuinely about that list (Table /
 * Cards, Columns). One group of view controls, next to the thing it views.
 *
 * Lives in the shell rather than `components/ui` because it reaches into the
 * preferences hooks; a design-system primitive must not depend on the app layer.
 */

import * as RadixPopover from '@radix-ui/react-popover';
import { LogOut } from 'lucide-react';
import type { ReactNode } from 'react';

import { api, type Me } from '../../app/api.js';
import { useSetDensity, useSetTheme, usePreferences } from '../../app/usePreferences.js';
import { DensityControl, ThemeControl } from '../../components/ui/index.js';

export function UserMenu({
  me,
  children,
}: {
  readonly me: Me;
  /** The trigger — the sidebar identity block, or the top-bar avatar. */
  readonly children: ReactNode;
}): ReactNode {
  const { density, theme } = usePreferences();
  const setDensity = useSetDensity();
  const setTheme = useSetTheme();

  return (
    <RadixPopover.Root>
      <RadixPopover.Trigger
        aria-label={`Account and preferences for ${me.displayName}`}
        className="rounded-ctl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700"
      >
        {children}
      </RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          align="end"
          sideOffset={8}
          className="z-30 w-64 rounded-card border border-card-line bg-card p-3 shadow-lg"
        >
          <p className="truncate text-body font-semibold text-ink">{me.displayName}</p>
          <p className="truncate text-meta text-ink2">{me.email}</p>

          <div className="mt-3 border-t border-hairline pt-3">
            <p className="pb-1.5 text-chip font-semibold uppercase tracking-wide text-ink2">
              Theme
            </p>
            <ThemeControl
              theme={theme}
              onChange={(next) => {
                setTheme.mutate(next);
              }}
            />
            <p className="pt-1.5 text-chip text-ink2">
              Device follows your computer or phone. Applies everywhere, on all your devices.
            </p>
          </div>

          <div className="mt-3 border-t border-hairline pt-3">
            <p
              id="density-label"
              className="pb-1.5 text-chip font-semibold uppercase tracking-wide text-ink2"
            >
              Density
            </p>
            <DensityControl
              density={density}
              onChange={(next) => {
                setDensity.mutate(next);
              }}
            />
            <p className="pt-1.5 text-chip text-ink2">
              Compact fits more rows in every list. Applies everywhere, on all your devices.
            </p>
          </div>

          <div className="mt-3 border-t border-hairline pt-2">
            <button
              type="button"
              onClick={() => {
                // A full navigation rather than a client-side route change: sign
                // out must leave nothing of this person's data in memory, and
                // reloading is the only way to be certain of that.
                void api('/api/v1/auth/sign-out', { method: 'POST' }).finally(() => {
                  window.location.assign('/');
                });
              }}
              className="flex min-h-(--tap-min) w-full items-center gap-2 rounded-ctl px-2 py-1.5 text-body text-ink hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-700"
            >
              <LogOut size={16} aria-hidden />
              Sign out
            </button>
          </div>
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}
